/**
 * Cliente HTTP del API de contenidos.
 *
 * Todas las llamadas de noticias y slides pasan por aquí, de modo que las
 * cabeceras, el manejo de errores y los tiempos de espera se tocan en un solo
 * sitio. Los servicios de arriba no saben nada de `fetch`.
 */

import { getAdminToken } from '../adminAuth';
import { resolveUrl } from './endpoints';

/** El API respondió, pero con un código de error. */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly url: string;
  /** Cuerpo de la respuesta, útil para enseñar el mensaje del backend. */
  readonly body: unknown;

  constructor(status: number, url: string, body: unknown) {
    super(`El API respondió ${status} en ${url}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

/** No hubo respuesta: caída de red, CORS o tiempo de espera agotado. */
export class ApiNetworkError extends Error {
  readonly url: string;

  constructor(url: string, cause: unknown) {
    super(`No se pudo contactar con el API (${url})`);
    this.name = 'ApiNetworkError';
    this.url = url;
    this.cause = cause;
  }
}

/** Corta la espera para que la interfaz no se quede colgada indefinidamente. */
const TIMEOUT_MS = 20_000;

/**
 * Cabeceras de autenticación.
 *
 * ÚNICO punto donde se decide cómo nos identificamos. Hoy se manda el token de
 * sesión del administrador; si el API acaba pidiendo `x-functions-key`, o las
 * dos cosas, se cambia aquí y ningún servicio se entera.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const cabeceras: Record<string, string> = {};

  // Asíncrono porque con Entra ID el token lo renueva MSAL en el momento; con
  // el login local se resuelve al instante.
  const token = await getAdminToken();
  if (token) cabeceras['Authorization'] = `Bearer ${token}`;

  const functionsKey = (import.meta.env.VITE_API_FUNCTIONS_KEY ?? '').trim();
  if (functionsKey) cabeceras['x-functions-key'] = functionsKey;

  return cabeceras;
}

interface PeticionOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Se serializa a JSON. Omitir en GET y DELETE. */
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Lanza una petición y devuelve el cuerpo ya parseado, todavía SIN validar.
 *
 * Devuelve `unknown` a propósito: quien llama tiene que pasar la respuesta por
 * el parser de `dto.ts` antes de usarla, para que un cambio en el API se
 * detecte aquí y no reventando un componente a mitad de render.
 */
export async function request(path: string, options: PeticionOptions): Promise<unknown> {
  const url = resolveUrl(path);
  const { method, body, signal } = options;

  // Se piden antes de arrancar el reloj de espera: renovar el token puede
  // implicar un viaje a Microsoft y no debe comerse el tiempo de la llamada.
  const cabecerasDeSesion = await authHeaders();

  // `AbortSignal.any` combina el corte por tiempo con el que traiga el llamante
  // (por ejemplo, el de un componente que se desmonta).
  const porTiempo = AbortSignal.timeout(TIMEOUT_MS);
  const corte = signal ? AbortSignal.any([signal, porTiempo]) : porTiempo;

  let respuesta: Response;

  try {
    // `url` es dinámica pero no arbitraria: el origen siempre es `BASE_URL`
    // (de `VITE_API_BASE_URL`, variable de entorno de compilación), y `path`
    // son siempre rutas fijas de `NOTICIAS_ENDPOINTS`/`SLIDES_ENDPOINTS`, con
    // cualquier parámetro (slug, id) pasado por `encodeURIComponent` antes de
    // interpolarse — no puede salirse del segmento de ruta ni cambiar de
    // origen. No hay entrada de usuario que decida a qué host se llama.
    respuesta = await fetch(url, {
      method,
      signal: corte,
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...cabecerasDeSesion,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new ApiNetworkError(url, error);
  }

  // 204 y 205 no traen cuerpo: intentar parsearlas daría un error engañoso.
  const sinCuerpo = respuesta.status === 204 || respuesta.status === 205;
  const contenido: unknown = sinCuerpo ? null : await leerCuerpo(respuesta);

  if (!respuesta.ok) throw new ApiHttpError(respuesta.status, url, contenido);

  return contenido;
}

/** Lee el cuerpo como JSON; si no lo es, lo devuelve como texto. */
async function leerCuerpo(respuesta: Response): Promise<unknown> {
  const texto = await respuesta.text();
  if (texto === '') return null;

  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return texto;
  }
}

export const get = (path: string, signal?: AbortSignal) => request(path, { method: 'GET', signal });

export const post = (path: string, body?: unknown, signal?: AbortSignal) =>
  request(path, { method: 'POST', body, signal });

export const put = (path: string, body: unknown, signal?: AbortSignal) =>
  request(path, { method: 'PUT', body, signal });

export const patch = (path: string, body: unknown, signal?: AbortSignal) =>
  request(path, { method: 'PATCH', body, signal });

export const del = (path: string, signal?: AbortSignal) =>
  request(path, { method: 'DELETE', signal });
